<template>
<div class="photo-selector" ref="photoSelector"
    :class="{'overflow-y-scroll': true}">
  <div v-if="uploadFile">
    <!-- 写真登録モード -->
    <div class="photo-add">
      <div class="canvas-wrapper">
        <canvas ref="previewCanvas"></canvas>
        <canvas v-show="false" ref="hiddenRealSizeCanvas"></canvas>
      </div>
    <div class="photo-add-bottom-bar ta-center">
        <button class="btn btn-lg btn-primary"
            :disabled="isUploadingPhoto"
            @click="addPhoto">
          <i class="fa fa-spinner fa-spin"
            v-show="isUploadingPhoto"></i>
          決定
        </button>
        <button class="btn btn-lg btn-default" @click="cancelAddPhoto">
          キャンセル
        </button>
      </div>
    </div>
  </div>
  <div v-if="isEditingPhoto">
    <!-- 編集モード -->
    <photo-editor
      :id="id"
      :photo="selectedPhoto"
      :photo-type="photoType"
      @done="doneEditPhoto"
      @cancel="cancelEditPhoto">
    </photo-editor>
  </div>
  <div v-if="isNormalMode">
    <!-- 通常モード -->
    <div class="photo-list">
      <div class="thumbnail" v-for="photo in photoList"
          :class="{
            'selected': photo.id === selectedPhotoId,
            'unselectable': !isSelectablePhoto(photo)
          }"
          :key="photo.id">
        <http-img class="photo-content"
          :src="photo.photo_url_medium"
          @click="selectPhoto(photo)">
        </http-img>
      </div>
      <div class="btn-add-photo">
        <label class="btn btn-lg btn-default" for="photoFileInput">
          写真<br>登録
        </label>
        <input type="file" @change="fileSelected"
          accept=".jpg, .jpeg, .png, .gif"
          style="display:none;"
          id="photoFileInput">
      </div>
    </div>
    <hr>
    <div class="selected-photo-area row2" v-if="selectedPhoto">
      <div class="col-xs-6">
        <div>
          <http-img class="photo-img"
            :src="selectedPhoto.photo_url_original">
          </http-img>
        </div>
        <div class="mt-4">
          <http-link class="btn btn-sm btn-default"
              :href="selectedPhoto.photo_url_original">
            <i class="fa fa-download"></i>
            ダウンロード
          </http-link>
        </div>
      </div>
      <div class="col-xs-6">
        <div v-if="showDeleteConfirm">
          <!-- 削除確認モード -->
          <div class="mb-10">
            <p v-if="!hasError">
              写真を削除します。よろしいですか？
            </p>
            <div v-if="hasError" class="err-msg create ta-left">
              <div v-for="msg in errorMessages" :key="msg">・{{ msg }}</div>
            </div>
          </div>
          <div class="ta-center">
            <button class="btn btn-lg btn-primary"
                @click="deletePhoto">
              OK
            </button>
            <button class="btn btn-lg btn-default"
                @click="closeShowDeleteConfirm">
              キャンセル
            </button>
          </div>
        </div>
        <div v-else>
          <button class="btn btn-lg btn-primary" @click="usePhoto">
            <i class="fa fa-chain"></i>
            決定
          </button>
          <button class="btn btn-lg btn-default" @click="editPhoto">
            <i class="fa fa-edit"></i>
            編集
          </button>
          <button class="btn btn-lg btn-danger"
              @click="showDeleteConfirm = true"
              v-show="!isDefaultSelectedPhoto(selectedPhoto)">
            <i class="fa fa-trash"></i>
            削除
          </button>
          <button class="btn btn-lg btn-default" @click="unusePhoto"
              v-show="isDefaultSelectedPhoto(selectedPhoto)">
            <i class="fa fa-chain-broken"></i>
            解除
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import EVT from '@/consts/eventNames'
import photoApi from '@/api/photo'
import imageMixin from '@/mixin/imageMixin'
import errorHandleMixin from '@/mixin/errorHandleMixin'
let api

const MODE_NORMAL = 'normal'
const MODE_ADD = 'add'
const MODE_EDIT = 'edit'

export default {
  name: 'photo-selector',
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    apiName: {
      type: String,
      required: true,
    },
    photos: {
      type: Array,
      required: true,
    },
    photoList: {
      type: Array,
      required: true,
    },
    defaultSelectedPhoto: {
      type: Object,
    },
    stream: {
      type: Object,
    },
    photoType: {
      type: String,
      required: true,
    },
    photoDate: {
      type: [String, Date],
      required: true,
    },
  },
  data() {
    return {
      uploadFile: null,
      selectedPhotoId: this.defaultSelectedPhoto.id,
      isUploadingPhoto: false,
      isEditingPhoto: false,
      showDeleteConfirm: false,
      onResizeFunc: () => {},

      errors: {},
    }
  },
  computed: {
    hasError() {
      return Object.keys(this.errors).length > 0
    },
    errorMessages() {
      const msgs = Object.keys(this.errors).map(k => {
        return this.errors[k]
      }, [])
      return Array.from(new Set([].concat(...msgs)))
    },
    selectedPhoto() {
      return this.photoList.find(p => p.id === this.selectedPhotoId)
    },
    isNormalMode() {
      return !this.uploadFile && !this.isEditingPhoto
    },
  },
  watch: {
    defaultSelectedPhoto() {
      if (!this.defaultSelectedPhoto ||
          !this.defaultSelectedPhoto.id) {
        this.selectedPhotoId = null
        return
      }
      this.selectedPhotoId = this.defaultSelectedPhoto.id
    },
    uploadFile() {
      if (!this.uploadFile) { return }
      this.loadImageWithExif(this.uploadFile.src)
        .then(image => {
          const previewCanvas = this.$refs.previewCanvas
          const hiddenRealSizeCanvas = this.$refs.hiddenRealSizeCanvas
          const maxHeight = window.innerHeight - 280
          const maxWidth = this.$refs.photoSelector.offsetWidth
          this.writeImageToCanvas(image, previewCanvas,
            { maxHeight, maxWidth })
          this.writeImageToCanvas(image, hiddenRealSizeCanvas)
        })
    },
  },
  mounted() {
    api = require(`@/api/${this.apiName}`).default
    this.setListeners()
  },
  destroyed() {
    window.removeEventListener('resize', this.onResizeFunc)
  },
  mixins: [imageMixin, errorHandleMixin],
  methods: {
    setMaxHeight() {
      this.$refs.photoSelector.style.maxHeight =
        (window.innerHeight - (60 + 70 + 50)) + 'px'
    },
    setListeners() {
      this.stream.recv(EVT.PHOTO.SELECTOR_RESET, () => {
        this.uploadFile = null
        this.isUploadingPhoto = false
        this.isEditingPhoto = false
        this.showDeleteConfirm = false
      })

      this.onResizeFunc = () => {
        this.setMaxHeight()
      }
      window.addEventListener('resize', this.onResizeFunc)
      this.onResizeFunc()
    },
    emitModeTransition(mode) {
      let titleStr = ''
      if (mode === MODE_NORMAL) {
        titleStr = '写真選択'
      } else if (mode === MODE_ADD) {
        titleStr = '写真登録'
      } else if (mode === MODE_EDIT) {
        titleStr = '写真編集'
      }
      this.$emit('mode-change', {
        mode: mode,
        title: titleStr,
      })
    },
    isSelectablePhoto(photo) {
      return this.photos.findIndex(p => p.id === photo.id) === -1 ||
        photo.id === (this.defaultSelectedPhoto || {}).id
    },
    isDefaultSelectedPhoto(photo) {
      return photo.id === (this.defaultSelectedPhoto || {}).id
    },
    selectPhoto(photo) {
      if (!this.isSelectablePhoto(photo)) { return }
      this.selectedPhotoId =
        this.selectedPhotoId === photo.id ? null : photo.id
    },
    fileSelected(e) {
      if (!e.target.files || e.target.files.length === 0) {
        return
      }
      const f = e.target.files[0]
      this.uploadFile = {
        name: f.name,
        type: f.type,
        file: f,
        src: window.URL.createObjectURL(f),
      }
      this.emitModeTransition(MODE_ADD)
    },
    cancelAddPhoto() {
      this.uploadFile = null
      this.emitModeTransition(MODE_NORMAL)
    },
    addPhoto: function() {
      this.isUploadingPhoto = true

      const formData = new FormData()
      formData.append('photo_date', this.photoDate)
      formData.append('photos[]',
        this.canvasToBlob(this.$refs.hiddenRealSizeCanvas),
        this.uploadFile.name)

      const obj = {
        formData: formData
      }
      photoApi.create(obj).then(() => {
        this.isUploadingPhoto = false
        this.uploadFile = null
        this.emitModeTransition(MODE_NORMAL)
        this.emitRefresh()
      }).catch(() => {
        this.isUploadingPhoto = false
      })
    },
    emitRefresh() {
      this.$emit('should-refresh')
    },
    usePhoto() {
      if (!this.selectedPhoto) { return }
      this.$emit('use-photo', this.selectedPhoto)
    },
    unusePhoto() {
      if (!this.selectedPhoto) { return }
      this.$emit('unuse-photo', this.selectedPhoto)
    },
    editPhoto() {
      if (!this.selectedPhoto) { return }
      this.isEditingPhoto = true
      this.emitModeTransition(MODE_EDIT)
    },
    doneEditPhoto(photo) {
      this.isEditingPhoto = false
      this.emitModeTransition(MODE_NORMAL)
      this.emitRefresh()
      this.selectedPhotoId = photo.id
    },
    cancelEditPhoto() {
      this.isEditingPhoto = false
      this.emitModeTransition(MODE_NORMAL)
    },
    closeShowDeleteConfirm() {
      this.errors = {}
      this.showDeleteConfirm = false
    },
    deletePhoto() {
      if (!this.selectedPhoto) { return }
      photoApi.delete({
        id: this.selectedPhoto.id,
      }).then(() => {
        this.selectedPhotoId = null
        this.showDeleteConfirm = false
        this.emitRefresh()
      }).catch(err => {
        this.handleErrorResponse(err)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.overflow-y-scroll {
  overflow-y: scroll;
}
.row1 {
  margin: 0 -4px;
}
.row2 {
  margin: 0 -10px;
}
.photo-list {
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
}
.thumbnail{
  width: 100px;
  height: 100px;
  padding: 4px;
  margin: 4px 8px;
  cursor: pointer;
  &.selected {
    border-color: red;
  }
  &.unselectable {
    opacity: 0.5;
    cursor: default;
  }
  .photo-content {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
.btn-add-photo {
  float: left;
  margin: 4px 8px;
}
.photo-img {
  max-height: 300px;
  max-width: 100%;
}
.photo-add-bottom-bar {
  margin-top: 8px;
}
</style>
